import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./videoCallWindow.module.css";
import ModalOverlay from "../modalOverlay/ModalOverlay";
import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { SocketContext } from "../../../SocketContext";
import { useBackgroundBlur } from "../../../hooks/useBackgroundBlur";
import incomingCallSound from "../../../sounds/incomingCallSound.wav";
import { toggleMuteMic, toggleVideo } from "../../../utils/utils";
import Modal from "../modal/Modal";
import MediaDevicesNotFoundErrorPopup from "../mediaDevicesNotFoundErrorPopup/mediaDevicesNotFoundErrorPopup";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../services/hooks";
import { chatConnectionSelector, chatUsersSelector } from "../../../services/selectors/chat.selector";
import { VIDEO_OFF_CODE, VIDEO_ON_CODE } from "../../../utils/constants";
import { IChatMessage } from "../../../services/types";
import Tooltip from "../../tooltip/tooltip";
import { loadAppConfig, resolveApiBaseUrl } from "../../../utils/config";

interface IVideoCallWindowProps {
  onClose: () => void;
  userGuid?: string | null;
  usersId?: number;
}

function formatRecordingDuration(seconds: number): string {
  const mm = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const ss = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
}

function VideoCallWindow({ onClose, userGuid, usersId }: IVideoCallWindowProps) {
  const {
    myVideo,
    stream,
    call,
    callAccepted,
    startCall,
    endCall,
    userVideo,
    anotherUserStream,
    setShowMediaDevicesNotFoundPopup,
    leaveCall,
    onRemoteCallEnded,
    onRemoteEndAcknowledged,
    replaceVideoTrack,
    startCallRecording,
    pauseCallRecording,
    finalizeCallRecording,
    cancelCallRecording,
    callRecordingState,
    callRecordingDurationSec,
    callRecordingUploadProgress,
    hasRemoteStream,
  } = useContext(SocketContext);

  const {
    isBlurEnabled,
    isLoading: isBlurLoading,
    isSupported: isBlurSupported,
    toggleBlur,
    blurredStream,
  } = useBackgroundBlur(stream, replaceVideoTrack);
  const navigate = useNavigate();
  const connection = useAppSelector(chatConnectionSelector);
  const chatUsers = useAppSelector(chatUsersSelector);

  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [patientVideoEnabled, setPatientVideoEnabled] = useState(true);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [showRecordingTooltip, setShowRecordingTooltip] = useState(false);
  const [showEndRecordingConfirm, setShowEndRecordingConfirm] = useState(false);

  const waitForAckRef = useRef(false);
  const callRecordingStateRef = useRef(callRecordingState);

  const patientUserId = usersId ?? call?.to?.id ?? 0;
  const guidFromChat = chatUsers.find((c) => c.userId === patientUserId)?.guid;

  const patientGuid = userGuid ?? call?.to?.guid ?? guidFromChat ?? null;

  const navigateToRecordTable = useCallback(
    (recordNumber?: number) => {
      navigate("/users/recordTable", {
        state: {
          Guid: patientGuid,
          UsersId: patientUserId,
          patientName: call?.to?.name,
          newRecordNumber: recordNumber,
        },
      });
      onClose();

    },
    [navigate, patientGuid, patientUserId, call?.to?.name, onClose]
  );

  const finalizeAndEndCall = useCallback(async () => {
    if (callRecordingState === "idle") {
      leaveCall();
      onClose();
      return;
    }
    setIsFinalizing(true);
    try {
      const result = await finalizeCallRecording();
      leaveCall();
      navigateToRecordTable(result?.recordNumber);
    } catch {
      leaveCall();
      onClose();
    } finally {
      setIsFinalizing(false);
    }
  }, [
    callRecordingState,
    finalizeCallRecording,
    leaveCall,
    navigateToRecordTable,
    onClose,
  ]);

  const handleConfirmSendRecording = useCallback(async () => {
    setShowEndRecordingConfirm(false);
    await finalizeAndEndCall();
  }, [finalizeAndEndCall]);

  const handleConfirmDiscardRecording = useCallback(async () => {
    setShowEndRecordingConfirm(false);
    await cancelCallRecording();
    leaveCall();
    onClose();
  }, [cancelCallRecording, leaveCall, onClose]);

  const handleHangUp = useCallback(() => {
    endCall();
    if (callRecordingState === "recording" || callRecordingState === "paused") {
      setShowEndRecordingConfirm(true);
    } else if (callRecordingState === "idle") {
      onClose();
    }
    // if "processing", the isFinalizing modal is already shown — do nothing
  }, [endCall, callRecordingState, onClose]);

  const [isStartingRecording, setIsStartingRecording] = useState(false);

  const beginRecording = useCallback(async (): Promise<void> => {
    if (!callAccepted || !patientGuid) return;
    if (callRecordingState !== "idle" && callRecordingState !== "paused") return;
    setIsStartingRecording(true);
    try {
      const config = await loadAppConfig();
      const apiBaseUrl = resolveApiBaseUrl(config).replace(/\/$/, "");
      await startCallRecording({
        patientGuid,
        patientName: call.to.name,
        patientUserId,
        apiBaseUrl,
      });
    } catch (err) {
      // console.error("[VideoCall] Failed to start/resume call recording:", err);
    } finally {
      setIsStartingRecording(false);
    }
  }, [callAccepted, patientGuid, callRecordingState, call.to.name, patientUserId, startCallRecording]);

  const handleStopRecording = useCallback(() => {
    if (callRecordingState !== "recording") return;
    pauseCallRecording();
  }, [callRecordingState, pauseCallRecording]);

  useEffect(() => {
    callRecordingStateRef.current = callRecordingState;
  }, [callRecordingState]);

  useEffect(() => {
    if (!onRemoteCallEnded) return;
    const handleRemoteEnd = () => {
      waitForAckRef.current = true;
    };
    const unsubscribe = onRemoteCallEnded(handleRemoteEnd);
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [onRemoteCallEnded]);

  useEffect(() => {
    if (!onRemoteEndAcknowledged) return;
    const handleAck = () => {
      if (!waitForAckRef.current) return;
      waitForAckRef.current = false;
      endCall();
      if (callRecordingStateRef.current === "recording" || callRecordingStateRef.current === "paused") {
        setShowEndRecordingConfirm(true);
      } else {
        leaveCall();
        onClose();
      }
    };
    const unsubscribe = onRemoteEndAcknowledged(handleAck);
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [
    onRemoteEndAcknowledged,
    endCall,
    leaveCall,
    onClose,
  ]);

  const windowRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selfViewPos, setSelfViewPos] = useState<{ x: number; y: number } | null>(null);
  const [isSelfViewDragging, setIsSelfViewDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const isRecording = callRecordingState === "recording";
  const isRecordingPaused = callRecordingState === "paused";
  const isRecordingActive = isRecording || isRecordingPaused || callRecordingState === "processing";
  const canStartRecording =
    callAccepted &&
    !!patientGuid &&
    (callRecordingState === "idle" || isRecordingPaused) &&
    !isStartingRecording &&
    !isFinalizing;
  const uploadTotal = callRecordingUploadProgress.total;
  const uploadUploaded = callRecordingUploadProgress.uploaded;

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      windowRef.current?.requestFullscreen().catch(() => { });
    } else {
      document.exitFullscreen().catch(() => { });
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const SELF_VIEW_W = 120;
  const SELF_VIEW_H = 180;

  const startSelfViewDrag = useCallback(
    (clientX: number, clientY: number, target: Element) => {
      isDraggingRef.current = true;
      setIsSelfViewDragging(true);
      const rect = target.getBoundingClientRect();
      dragOffsetRef.current = { x: clientX - rect.left, y: clientY - rect.top };
    },
    []
  );

  const handleSelfViewMouseDown = (e: React.MouseEvent<HTMLVideoElement>) => {
    e.preventDefault();
    startSelfViewDrag(e.clientX, e.clientY, e.currentTarget);
  };

  const handleSelfViewTouchStart = (e: React.TouchEvent<HTMLVideoElement>) => {
    const touch = e.touches[0];
    startSelfViewDrag(touch.clientX, touch.clientY, e.currentTarget);
  };

  useEffect(() => {
    const moveDrag = (clientX: number, clientY: number) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const container = containerRef.current.getBoundingClientRect();
      setSelfViewPos({
        x: Math.max(
          0,
          Math.min(
            clientX - container.left - dragOffsetRef.current.x,
            container.width - SELF_VIEW_W
          )
        ),
        y: Math.max(
          0,
          Math.min(
            clientY - container.top - dragOffsetRef.current.y,
            container.height - SELF_VIEW_H
          )
        ),
      });
    };

    const handleMouseMove = (e: MouseEvent) => moveDrag(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      moveDrag(e.touches[0].clientX, e.touches[0].clientY);
    };
    const endDrag = () => {
      isDraggingRef.current = false;
      setIsSelfViewDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", endDrag);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", endDrag);
    };
  }, []);

  useEffect(() => {
    if (myVideo.current) {
      myVideo.current.srcObject = (blurredStream ?? stream) as MediaProvider;
    }
    if (anotherUserStream && userVideo.current) {
      userVideo.current.srcObject = anotherUserStream as MediaProvider;
    }
    return () => {
      if (stream) {
        stream.getVideoTracks().forEach((track) => {
          track.enabled = true;
        });
        setIsVideoEnabled(true);
      }
    };
  }, [stream, blurredStream, anotherUserStream]);

  useEffect(() => {
    if (patientVideoEnabled && anotherUserStream && userVideo.current) {
      userVideo.current.srcObject = anotherUserStream as MediaProvider;
    }
  }, [patientVideoEnabled, anotherUserStream]);

  useEffect(() => {
    if (!connection) return;
    const handler = (message: IChatMessage) => {
      const content: string | undefined = message?.content;
      if (!content) return;
      if (content.includes(VIDEO_OFF_CODE)) setPatientVideoEnabled(false);
      if (content.includes(VIDEO_ON_CODE)) setPatientVideoEnabled(true);
    };
    connection.on("onMessage", handler);
    return () => connection.off("onMessage", handler);
  }, [connection]);

  if (!stream)
    return (
      <Modal onClose={() => setShowMediaDevicesNotFoundPopup(false)}>
        <MediaDevicesNotFoundErrorPopup
          onClose={() => setShowMediaDevicesNotFoundPopup(false)}
        />
      </Modal>
    );

  return (
    <>
      <div className={`${styles.window}`} ref={windowRef} onDoubleClick={toggleFullscreen}>
        {!callAccepted && <audio src={incomingCallSound} autoPlay={true} />}
        <div className={styles.videoContainer} ref={containerRef}>
          <h3 className={styles.header}>
            {t("VIDEO_CALL_WINDOW_CALLING_PREFIX")} {call?.to?.name}
          </h3>
          <button
            type="button"
            className={styles.fullscreenBtn}
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
            title={isFullscreen ? "יציאה ממסך מלא" : "מסך מלא"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="10" y1="14" x2="3" y2="21" />
                <line x1="21" y1="3" x2="14" y2="10" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
          {stream && (
            <>
              <video
                playsInline
                ref={myVideo}
                muted
                autoPlay
                className={`${styles.myVideo} ${isSelfViewDragging ? styles.myVideoDragging : ""}`}
                style={
                  selfViewPos
                    ? {
                      left: selfViewPos.x,
                      top: selfViewPos.y,
                      right: "auto",
                      cursor: isSelfViewDragging ? "grabbing" : "grab",
                    }
                    : { cursor: "grab" }
                }
                onMouseDown={handleSelfViewMouseDown}
                onTouchStart={handleSelfViewTouchStart}
              />
              <div className={styles.userVideoWindow}>
                {startCall && !callAccepted && (
                  <div className={styles.outgoingCall}>
                    <div className={styles.pulse} />
                  </div>
                )}
                {callAccepted && (
                  <>
                    {isRecordingActive && (
                      <div className={styles.recordingIndicator} aria-live="polite">
                        <span className={isRecordingPaused ? styles.recordingDotPaused : styles.recordingDot} />
                        <span className={styles.recordingTime}>
                          {formatRecordingDuration(callRecordingDurationSec)}
                        </span>
                        {uploadTotal > 0 && (
                          <span className={styles.recordingUpload}>
                            {uploadUploaded}/{uploadTotal}
                          </span>
                        )}
                      </div>
                    )}
                    {patientVideoEnabled && hasRemoteStream ? (
                      <video
                        playsInline
                        ref={userVideo}
                        autoPlay
                        className={styles.userVideo}
                      />
                    ) : (
                      <div
                        className={styles.userVideo}
                        style={{
                          backgroundColor: "#000",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          width: "100%",
                        }}
                      >
                        <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.7 }}>
                          📹
                        </div>
                        <div style={{ fontSize: "24px" }}>המצלמה כבויה</div>
                      </div>
                    )}
                  </>
                )}
                <div className={styles.buttonsBar}>
                  <button
                    type="button"
                    className={`hover ${styles.btn}`}
                    onClick={() => toggleMuteMic(stream, setIsMicMuted)}
                  >
                    <span
                      className={`${styles.btn_type_mute} ${isMicMuted && styles.btn_type_unmute
                        }`}
                    />
                  </button>
                  <button
                    type="button"
                    className={`hover ${styles.btn}`}
                    onClick={() => toggleVideo(stream, setIsVideoEnabled)}
                  >
                    <span
                      className={`${styles.btn_type_mute} ${styles.btn_type_video} ${!isVideoEnabled && styles.btn_type_video_inactive
                        }`}
                    />
                  </button>
                  <button
                    type="button"
                    className={`hover ${styles.btn}`}
                    onClick={toggleBlur}
                    disabled={isBlurSupported === false || isBlurLoading}
                  >
                    <span
                      className={`${styles.btn_type_blur} ${isBlurEnabled ? styles.btn_type_blur_active : ""
                        } ${isBlurSupported === false ? styles.btn_type_blur_disabled : ""}`}
                    >
                      {isBlurLoading && <span className={styles.btn_blur_spinner} />}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`hover ${styles.btn} btn_type_endCall`}
                    disabled={isFinalizing}
                    onClick={handleHangUp}
                  />
                  {callAccepted && (callRecordingState === "idle" || isRecordingPaused) && (
                    <button
                      className={`hover ${styles.btn}`}
                      type="button"
                      disabled={!canStartRecording}
                      onClick={beginRecording}
                      onMouseEnter={() => setShowRecordingTooltip(true)}
                      onMouseLeave={() => setShowRecordingTooltip(false)}
                      aria-label={isRecordingPaused ? (t("RESUME_RECORDING") || "Resume recording") : (t("AUDIO_RECORDING") || "Start recording")}
                    >
                      <span className={styles.btn_type_record}>
                        <span className={styles.btn_type_record_inside} />
                        <Tooltip isShown={showRecordingTooltip} position="absolute">
                          {isRecordingPaused ? (t("RESUME_RECORDING") || "המשך הקלטה") : (t("AUDIO_RECORDING") || "התחל הקלטה")}
                        </Tooltip>
                      </span>
                    </button>
                  )}
                  {callAccepted && isRecording && (
                    <button
                      className={`hover ${styles.btn}`}
                      type="button"
                      disabled={isFinalizing}
                      onClick={handleStopRecording}
                      onMouseEnter={() => setShowRecordingTooltip(true)}
                      onMouseLeave={() => setShowRecordingTooltip(false)}
                      aria-label={t("STOP_RECORDING") || "Stop recording"}
                    >
                      <span className={styles.btn_type_record}>
                        <span className={styles.btn_type_stop_record} />
                        <Tooltip isShown={showRecordingTooltip} position="absolute">
                          {t("STOP_RECORDING")}
                        </Tooltip>
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {isFinalizing && (
        <Modal onClose={() => { }}>
          <div className={styles.show_data}>
            <h2 className={styles.title}>{t("FINISHING_RECORDING")}</h2>
            <p className={styles.message}>{t("UPLOADING_AND_PROCESSING")}</p>
          </div>
        </Modal>
      )}
      {showEndRecordingConfirm && (
        <Modal onClose={() => { }}>
          <div className={styles.show_data}>
            <h2 className={styles.title}>
              {t("RECORDING_CONFIRM_SEND_TITLE") || "שליחת ההקלטה"}
            </h2>
            <p className={styles.message}>
              {t("RECORDING_CONFIRM_SEND_TEXT") || "יש לך הקלטה של"}{" "}
              <strong>{formatRecordingDuration(callRecordingDurationSec)}</strong>
              {". "}
              {t("RECORDING_CONFIRM_SEND_QUESTION") || "לשלוח לשרת?"}
            </p>
            <div className={styles.actions}>
              <button
                className={`${styles.btnModal} ${styles.primary}`}
                onClick={handleConfirmSendRecording}
              >
                {t("YES") || "כן"}
              </button>
              <button
                className={`${styles.btnModal} ${styles.secondary}`}
                onClick={handleConfirmDiscardRecording}
              >
                {t("NO") || "לא"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      <ModalOverlay onClick={onClose} />
    </>
  );
}

export default VideoCallWindow;
